import React from "react";
import useShow from "../hooks/useShow";
import { get_opporunity } from "../../../service/api/opportunity";
import { Link, useParams } from "react-router-dom";
import { setTitle } from "../../../store/slices/navbar";
import { useAppDispatch, useAppSelector } from "../../../store";
import { formatDate } from "../../../service/convert/date";
import { IconFileText } from "@tabler/icons-react";
import { Loader } from "../../../components/animation/Loader";

const OpportunityDetails = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const opportunity = useAppSelector(
    ({ opportunity }) => opportunity.opportunity,
  );
  const loading = useAppSelector(({ opportunity }) => opportunity.loading);

  const { image } = useShow();

  const onLoad = React.useCallback(() => {
    dispatch(setTitle("Opportunity"));
    if (id) get_opporunity(dispatch, id);
  }, [dispatch, id]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="overflow-y-auto">
      <div className="grid lg:grid-cols-3 items-start gap-6">
        {/* Image Section */}
        <div className="lg:col-span-2 bg-white shadow-sm rounded-xl overflow-hidden">
          <div
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            className="relative h-[400px] w-full bg-gray-100"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-dark/80 to-transparent" />
            <span
              className={`absolute top-4 right-4 text-sm font-medium rounded-full px-3 py-1 ${
                opportunity?.status === "1"
                  ? "text-primary-800 bg-customgreen"
                  : "text-red-800 bg-red-200/50"
              }`}
            >
              {opportunity?.status === "1" ? "Active" : "Expired"}
            </span>
          </div>
          <div className="p-6 space-y-6">
            <h1 className="text-3xl font-semibold text-gray-900">
              {opportunity?.opportunity_name}
            </h1>

            <hr className="w-3/12" />

            <div
              dangerouslySetInnerHTML={{
                __html: opportunity?.opportunity_description || "",
              }}
              className="max-w-none text-gray-700 
                           [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-4 
                           [&>h2]:text-xl [&>h2]:font-semibold [&>h2]:mb-3
                           [&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4"
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 flex flex-col bg-white shadow-sm rounded-xl">
          <p className="flex items-center justify-between text-md font-medium text-gray-500 mb-6">
            <span> Opportunity files</span>
            {opportunity?.expire_date && (
              <span className="font-semibold">
                {formatDate(opportunity.expire_date)}
              </span>
            )}
          </p>

          <div className="space-y-4">
            {(opportunity?.opportunities_files || []).length === 0 ? (
              <p className="text-sm text-gray-500">No files uploaded.</p>
            ) : (
              <>
                {(opportunity?.opportunities_files || []).map((file, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        {/* Document Icon */}
                        <div className="flex-shrink-0 bg-primary-50/20 p-2 rounded-md text-primary-600">
                          <IconFileText />
                        </div>

                        {/* Document Details */}
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900">
                            Opportunity file {index + 1}
                          </h3>
                        </div>
                      </div>

                      {/* View Button */}
                      <Link
                        to={file}
                        target="_blank"
                        className="ml-4 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetails;
